import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { COACH_MARKETPLACE_PAGE } from 'constants/pages';
import {
  GenderEnum,
  GetCoachesByGeoQuery,
  SportsEnum,
  useGetCoachesByGeoLazyQuery,
} from 'types/generated/client';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { milesToMeters } from 'utils/shared/geo/milesToMeters';
import { convertUnitAmountToFormattedPrice } from 'utils/shared/money/convertFormattedPriceToUnitAmount';
import { isWithinPriceRange } from 'utils/shared/money/isWithinPriceRange';
import { useGeoLocation } from 'hooks/useGeoLocation';
import { useQueryParam } from 'hooks/useQueryParam';
import { useVairProBadges } from 'hooks/useVairProBadges';
import { LessonPageModeEnum } from 'screens/ExploreLessons/enums';
import { SortByEnum } from 'screens/ExploreRemote/enums';
import { GoogleMapProps } from 'components/GoogleMap/props';
import LessonFilters from './LessonFiltersModal/LessonFilters';
import LessonList from './LessonList';
import LessonMapMarker from './LessonMapMarker';
import { CoachAvailabilityFilter, MAX_PRICE, MIN_PRICE, WORKS_WITH_OPTIONS } from './constants';
import { CertificationEnum, UseLessonProps } from './props';
import {
  VENUE_DISTANCE_IMPERIAL_OPTIONS,
  coachHasPhoto,
  isCoachProfile,
  isInstantBookEligible,
} from './utils';

const useLesson = ({
  pageMode = LessonPageModeEnum.EXPLORE,
  initialSport,
  initialCityCoordinates,
  serverCoachData,
}: UseLessonProps) => {
  const { centerLatitude, centerLongitude, isExactCenter, position } = useGeoLocation();

  const [getCoachesByGeoLazyQuery, { data, loading }] = useGetCoachesByGeoLazyQuery();
  const [listHoveredCoachId, setListHoveredCoachId] = useState<string | null>(null);
  const [activeCoachId, setActiveCoachId] = useState<string | null>(null);

  const router = useRouter();

  const getInitialSport = () => {
    // If initialSport is provided from server-side data, use it
    if (initialSport) {
      return initialSport;
    }

    if (pageMode === LessonPageModeEnum.EXPLORE) {
      return SportsEnum.Pickleball;
    } else if (pageMode === LessonPageModeEnum.PTR) {
      return SportsEnum.Tennis;
    } else if (pageMode === LessonPageModeEnum.PPR) {
      return SportsEnum.Pickleball;
    } else if (pageMode === LessonPageModeEnum.PCR) {
      return SportsEnum.Padel;
    }
    return SportsEnum.Pickleball;
  };

  const [selectedSport, setSelectedSport, isSportReady] = useQueryParam('sport', {
    defaultValue: getInitialSport(),
    parser: (value: string) => {
      const sport = Object.values(SportsEnum).find(
        (val) => val.toLowerCase() === value.toLowerCase(),
      );
      return sport || getInitialSport();
    },
    serializer: (value: SportsEnum) => value.toLowerCase(),
  });
  const [selectedGender, setSelectedGender] = useState<GenderEnum | null>(null);
  const [minPrice, setMinPrice] = useState<number>(MIN_PRICE);
  const [maxPrice, setMaxPrice] = useState<number>(MAX_PRICE);
  const [selectedCertification, setSelectedCertification] = useState<CertificationEnum | null>(
    null,
  );
  const [selectedWorksWith, setSelectedWorksWith] = useState<string | null>(null);
  // TODO(schema): there is no certification level on the coach profile yet —
  const [sortBy, setSortBy] = useState<SortByEnum>(SortByEnum.Followers);
  // Mobile-only tab. Desktop shows every coach and splits instant-book eligible ones
  // into their own "Top coaches" section instead of filtering them out.
  const [availability, setAvailability] = useState<CoachAvailabilityFilter>(
    CoachAvailabilityFilter.All,
  );
  const [isOpenMap, setIsOpenMap] = useState(false);

  const [distance, setDistance] = useState(VENUE_DISTANCE_IMPERIAL_OPTIONS[0]);

  // Use server data if available, otherwise use client data. `serverCoachData.coaches`
  // is typed from the server-side `getCityCoaches` query, whose generated types are a
  // separate (nominal) copy of the schema — the same coach shape at runtime, but not
  // assignable to the client `CoachType`. Cast at this boundary so the rest of the hook
  // works against a single client-typed coach list.
  const coachesOnPlatform: GetCoachesByGeoQuery['userProfiles'] =
    (serverCoachData?.coaches as unknown as GetCoachesByGeoQuery['userProfiles']) ||
    data?.userProfiles ||
    [];

  const decoratedCoaches = useMemo(() => {
    // Use initial city coordinates if available, otherwise use user's current location
    const baseLat = initialCityCoordinates?.latitude || centerLatitude || 0;
    const baseLng = initialCityCoordinates?.longitude || centerLongitude || 0;

    // Combine both coach types and add distance calculation
    const allCoachesWithDistance = [...coachesOnPlatform].map((coach) => {
      return {
        ...coach,
        distance: calculateHaversineDistance({
          coord1: {
            latitude: baseLat,
            longitude: baseLng,
          },
          coord2: {
            latitude: coach.geometry.coordinates[1],
            longitude: coach.geometry.coordinates[0],
          },
          unit: 'miles',
        }),
      };
    });

    return allCoachesWithDistance;
  }, [coachesOnPlatform, centerLatitude, centerLongitude, initialCityCoordinates]);

  const filteredCoaches = useMemo(() => {
    return decoratedCoaches.filter((coach) => {
      if (selectedGender && coach.gender && coach.gender !== selectedGender) {
        return false;
      }

      // Filter by price range
      const price = coach.priceUnitAmountCoachDefault!;
      const currency = coach.stripeMerchantCurrency || coach.currencyCoachDefault;
      const normalisedPrice = convertUnitAmountToFormattedPrice(price, currency!);
      if (
        !isWithinPriceRange(normalisedPrice, {
          minimum: minPrice,
          maximum: maxPrice,
          sliderMaximum: MAX_PRICE,
        })
      ) {
        return false;
      }

      if (pageMode === LessonPageModeEnum.EXPLORE) {
        // VAIR PRO is not a racquet-registry certification — it is narrowed separately
        // in `certifiedCoaches` using the VAIR API's `isVairPro`, so it must not be
        // held to the registry flag here.
        const isRegistryCertification =
          !!selectedCertification && selectedCertification !== CertificationEnum.VAIR_PRO;

        if (isRegistryCertification && !coach.isProfessionalRacquetRegistryActive) {
          return false;
        }
      } else {
        if (!coach.isProfessionalRacquetRegistryActive) {
          return false;
        }
      }

      // "Works with" — one option at a time, matched against either the coach's age
      // groups (Adults/Kids/Seniors) or their skill levels (Beginners/…/Experts)
      // depending on which the option came from.
      if (selectedWorksWith) {
        const option = WORKS_WITH_OPTIONS.find((worksWith) => worksWith.id === selectedWorksWith);

        if (option?.kind === 'skillLevel') {
          const hasSkillLevel = (coach.coachLessonSkillLevels ?? []).some(
            (skillLevel) => skillLevel.skillLevel === option.value,
          );
          if (!hasSkillLevel) {
            return false;
          }
        }

        if (option?.kind === 'ageGroup') {
          const hasAgeGroup = (coach.coachLessonAgeGroups ?? []).some(
            (ageGroup) => ageGroup.lessonAgeGroup === option.value,
          );
          if (!hasAgeGroup) {
            return false;
          }
        }
      }

      // Filter by sport
      const isSelectedDefaultSport = coach.defaultSport === selectedSport;
      const isSelectedSport = coach.coachSports?.some((sport) => sport.sport === selectedSport);

      if (selectedSport && !isSelectedDefaultSport && !isSelectedSport) {
        return false;
      }

      return true;
    });
  }, [
    decoratedCoaches,
    selectedGender,
    minPrice,
    maxPrice,
    selectedCertification,
    selectedWorksWith,
    selectedSport,
    pageMode,
  ]);

  const coaches = useMemo(() => {
    // Make a copy to avoid mutating the original array
    const sortedCoaches = [...filteredCoaches];

    switch (sortBy) {
      case SortByEnum.Followers:
        return sortedCoaches.toSorted((a, b) => {
          // Only apply to coach profiles that have followers data
          if (isCoachProfile(a) && isCoachProfile(b)) {
            return (
              (b.followersAggregate?.aggregate?.count || 0) -
              (a.followersAggregate?.aggregate?.count || 0)
            );
          }
          // Keep starter profiles at the end
          if (isCoachProfile(a) && !isCoachProfile(b)) return -1;
          if (!isCoachProfile(a) && isCoachProfile(b)) return 1;
          return 0;
        });

      case SortByEnum.PriceLowHigh:
        return sortedCoaches.toSorted((a, b) => {
          if (isCoachProfile(a) && isCoachProfile(b)) {
            return (a.priceUnitAmountCoachDefault || 0) - (b.priceUnitAmountCoachDefault || 0);
          }
          // Keep starter profiles at the end
          if (isCoachProfile(a) && !isCoachProfile(b)) return -1;
          if (!isCoachProfile(a) && isCoachProfile(b)) return 1;
          return 0;
        });

      case SortByEnum.PriceHighLow:
        return sortedCoaches.toSorted((a, b) => {
          if (isCoachProfile(a) && isCoachProfile(b)) {
            return (b.priceUnitAmountCoachDefault || 0) - (a.priceUnitAmountCoachDefault || 0);
          }
          // Keep starter profiles at the end
          if (isCoachProfile(a) && !isCoachProfile(b)) return -1;
          if (!isCoachProfile(a) && isCoachProfile(b)) return 1;
          return 0;
        });

      case SortByEnum.Distance:
        return sortedCoaches.toSorted((a, b) => a.distance - b.distance);
    }
  }, [filteredCoaches, sortBy]);

  // Resolve which coaches are VAIR PROs, both so the card can show the badge and so
  // the VAIR PRO certification tile can filter on it. Starter profiles have no bounce
  // user account, so they're excluded.
  //
  // Deliberately keyed off `coaches` rather than the VAIR-filtered list below: sourcing
  // it from the filtered output would shrink the id set, refetch a smaller map, and
  // shrink the list again on every pass.
  const coachUserIds = useMemo(
    () =>
      coaches
        .filter(isCoachProfile)
        .map((coach) => coach.id)
        .filter((id): id is string => !!id),
    [coaches],
  );

  const vairProMap = useVairProBadges(coachUserIds);

  /**
   * VAIR PRO is a separate programme from the racquet registry, and its status lives
   * on the VAIR API rather than on `UserProfiles` — so it cannot be applied alongside
   * the other certifications in `filteredCoaches` and is narrowed here instead.
   */
  const certifiedCoaches = useMemo(() => {
    if (selectedCertification !== CertificationEnum.VAIR_PRO) {
      return coaches;
    }

    return coaches.filter((coach) => (coach.id ? !!vairProMap[coach.id] : false));
  }, [coaches, selectedCertification, vairProMap]);

  const instantBookEligibleCoaches = useMemo(() => {
    const filtered = certifiedCoaches.filter((coach) => {
      return isCoachProfile(coach) ? isInstantBookEligible(coach) : false;
    });

    if (pageMode !== LessonPageModeEnum.EXPLORE) {
      return filtered;
    }

    // Two-tier: coaches with photos first, then without — existing sort preserved within each tier
    const withPhotos = filtered.filter(coachHasPhoto);
    const withoutPhotos = filtered.filter((coach) => !coachHasPhoto(coach));
    return [...withPhotos, ...withoutPhotos];
  }, [certifiedCoaches, pageMode]);

  const nonInstantBookEligibleCoaches = useMemo(() => {
    // The "Instant book" tab drops this tier entirely, which cascades to the display
    // list, the map and the result count via `allCoaches` below.
    if (availability === CoachAvailabilityFilter.InstantBook) {
      return [];
    }

    return certifiedCoaches.filter((coach) => {
      return isCoachProfile(coach) ? !isInstantBookEligible(coach) : true;
    });
  }, [certifiedCoaches, availability]);

  const MIN_COACHES_WITH_PHOTOS = 5;

  const displayNonInstantBookCoaches = useMemo(() => {
    if (pageMode !== LessonPageModeEnum.EXPLORE) {
      return nonInstantBookEligibleCoaches;
    }

    const instantBookWithPhotoCount = instantBookEligibleCoaches.filter(coachHasPhoto).length;
    const nonInstantWithPhotos = nonInstantBookEligibleCoaches.filter(coachHasPhoto);
    const totalWithPhotos = instantBookWithPhotoCount + nonInstantWithPhotos.length;

    if (totalWithPhotos >= MIN_COACHES_WITH_PHOTOS) {
      return nonInstantWithPhotos;
    }

    // Not enough coaches with photos — show all, but photos first
    const nonInstantWithoutPhotos = nonInstantBookEligibleCoaches.filter(
      (coach) => !coachHasPhoto(coach),
    );
    return [...nonInstantWithPhotos, ...nonInstantWithoutPhotos];
  }, [instantBookEligibleCoaches, nonInstantBookEligibleCoaches, pageMode]);

  const fetchCoaches = ({
    longitude,
    latitude,
    distance,
  }: {
    longitude: number;
    latitude: number;
    distance: number;
  }) => {
    getCoachesByGeoLazyQuery({
      fetchPolicy: 'network-only',
      variables: {
        distance,
        from: {
          type: 'Point',
          coordinates: [longitude, latitude],
        },
      },
    });
  };

  const handleClearAllFilters = () => {
    // Reset all filters to their default values
    setSelectedSport(SportsEnum.Pickleball);
    setSelectedCertification(null);
    setSelectedGender(null);
    setSelectedWorksWith(null);
    setMinPrice(MIN_PRICE);
    setMaxPrice(MAX_PRICE);
    setDistance(VENUE_DISTANCE_IMPERIAL_OPTIONS[0]);
    setAvailability(CoachAvailabilityFilter.All);
  };

  const navigateToCoachMarketplace = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    if (serverCoachData)
      router.push(
        `${COACH_MARKETPLACE_PAGE}?sport=${selectedSport.toLocaleLowerCase()}&lat=${latitude}&lng=${longitude}`,
      );
  };

  // All coaches for map display and interaction tracking
  const allCoaches = useMemo(() => {
    return [...instantBookEligibleCoaches, ...nonInstantBookEligibleCoaches];
  }, [instantBookEligibleCoaches, nonInstantBookEligibleCoaches]);

  const activeCoach = allCoaches.find((coach) => coach.id === activeCoachId);
  const hoveredCoach = allCoaches.find((coach) => coach.id === listHoveredCoachId);

  const lessonMapProps: GoogleMapProps = {
    syncDragToURL: !serverCoachData,
    center: {
      lat: initialCityCoordinates?.latitude || centerLatitude,
      lng: initialCityCoordinates?.longitude || centerLongitude,
    },

    options: {
      zoomControl: true,
      streetViewControl: false,
      mapTypeControl: false,
      disableDefaultUI: true,
      clickableIcons: false,
    },

    centerIcon: (
      <div className="h-8 w-8 rounded-full border-[12px] border-color-border-brand bg-white" />
    ),

    zoom: 12,
    markers: allCoaches.map((coach) => ({
      lat: coach.geometry.coordinates[1],
      lng: coach.geometry.coordinates[0],
      markerId: coach.id,

      zIndex:
        activeCoach && activeCoach.id === coach.id
          ? 3
          : hoveredCoach && hoveredCoach.id === coach.id
          ? 2
          : 1,
      children: (
        <LessonMapMarker
          coach={coach}
          activeCoach={activeCoach}
          setActiveCoachId={setActiveCoachId}
          listHoveredCoachId={listHoveredCoachId}
          setListHoveredCoachId={setListHoveredCoachId}
        />
      ),
    })),
    isExactCenter,
    onCenterChange: ({ lat, lng }) => {
      navigateToCoachMarketplace({ latitude: lat, longitude: lng });
    },
  };

  useEffect(() => {
    setSelectedCertification(null);
  }, [selectedSport]);

  useEffect(() => {
    // Only fetch coaches if server data is not available
    if (!serverCoachData && position) {
      fetchCoaches({
        longitude: position.longitude,
        latitude: position.latitude,
        distance: milesToMeters(distance.id),
      });
    }
  }, [position, distance, serverCoachData]);

  const handleToggleMap = () => {
    setIsOpenMap((curr) => !curr);
  };

  return {
    LessonList: (
      <LessonList
        instantBookEligibleCoaches={instantBookEligibleCoaches}
        nonInstantBookEligibleCoaches={displayNonInstantBookCoaches}
        allCoaches={coaches} // Pass the original sorted coaches for non-EXPLORE modes
        listHoveredCoachId={listHoveredCoachId}
        setListHoveredCoachId={setListHoveredCoachId}
        loading={loading}
        activeSport={selectedSport}
        pageMode={pageMode}
        onClearFilters={handleClearAllFilters}
        vairProMap={vairProMap}
      />
    ),
    LessonFilters: (
      <LessonFilters
        setPriceMinimum={setMinPrice}
        priceMinimum={minPrice}
        setPriceMaximum={setMaxPrice}
        priceMaximum={maxPrice}
        setSelectedGender={setSelectedGender}
        selectedGender={selectedGender}
        setSelectedWorksWith={setSelectedWorksWith}
        selectedWorksWith={selectedWorksWith}
        setSelectedCertification={setSelectedCertification}
        selectedCertification={selectedCertification}
        setDistance={setDistance}
        distance={distance}
        setSortBy={setSortBy}
        sortBy={sortBy}
        selectedSport={selectedSport}
        pageMode={pageMode}
        availability={availability}
        setAvailability={setAvailability}
      />
    ),
    lessonMapProps,
    distance,
    setDistance,
    selectedSport,
    setSelectedSport,
    handleClearAllFilters,
    setSortBy,
    sortBy,
    availability,
    setAvailability,
    lessonsLength: allCoaches.length,
    isSportReady,
    isOpenMap,
    handleToggleMap,
    onAddressChange: ({ latitude, longitude }: { latitude: number; longitude: number }) => {
      navigateToCoachMarketplace({ latitude, longitude });
    },
  };
};

export default useLesson;
